import React from 'react';
import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  0% { opacity: 0; }
  100% { opacity: 1; }
`;

const slideIn = keyframes`
  0% { transform: translateX(100%); }
  100% { transform: translateX(0); }
`;

const typewriter = keyframes`
  0% { width: 0; }
  100% { width: 100%; }
`;

const hoverIcon = keyframes`
  0% { transform: scale(1); }
  100% { transform: scale(1.1); }
`;

const bounceIcon = keyframes`
  0% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0); }
`;

const zoomImage = keyframes`
  0% { transform: scale(1); }
  100% { transform: scale(1.05); }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  min-height: 110vh;
  padding: 20px 10px;
  animation: ${fadeIn} 1.5s ease-in-out;

  @media screen and (min-width: 768px) {
    flex-direction: row;
    justify-content: center;
    padding: 0;
  }
`;

const TextContainer = styled.div`
  flex: 1;
  text-align: center;
  padding: 0 10px;
  opacity: 0;
  animation: ${fadeIn} 2s ease-in-out 0.5s forwards;

  @media screen and (min-width: 768px) {
    text-align: left;
    padding: 0 40px;
  }
`;

const ImageContainer = styled.div`
  flex: 1;
  margin-bottom: 30px;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  animation: ${slideIn} 1.5s ease-out;

  @media screen and (min-width: 768px) {
    margin-bottom: 0;
    margin-right: 20px;
  }
`;

const Image = styled.img`
  width: 100%;
  max-width: 420px;
  height: auto;
  object-fit: cover;
  border-radius: 20px;
  opacity: 0;
  animation: ${fadeIn} 1.5s ease-in-out 1s forwards;
  transition: transform 0.4s ease;

  &:hover {
    transform: scale(1.05);
  }

  @media screen and (min-width: 768px) {
    max-width: 480px;
  }
`;


const BackgroundCard = styled.div`
  position: absolute;
  max-width: 490px;
  top: -20px;      // starts slightly below image top
  left: 80px;     // starts slightly after image left
  width: 100%;    // slightly wider than image
  height: 100%;   // slightly taller than image
  background-color: #333;
  border-radius: 30px;
  z-index: -1;
  opacity: 0;
  animation: ${fadeIn} 2s ease-in-out 1.5s forwards;

  @media screen and (max-width: 768px) {
    width: 105%;
    height: 105%;
    top: 15px;
    left: 10px;
  }
`;



const Title = styled.h1`
  font-weight: 400;
  font-size: 2.2rem;
  margin-bottom: 20px;
  width: 100%;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  animation: ${typewriter} 3s steps(30) 1s forwards;
  opacity: 0;
  animation: ${fadeIn} 2s ease-in-out 1.2s forwards;
`;

const Subtitle = styled.p`
  margin-bottom: 20px;
  font-size: 1.2rem;
  text-align: justify;
  opacity: 0;
  animation: ${fadeIn} 2s ease-in-out 1.8s forwards;
`;

const Description = styled.p`
  font-weight: 300;
  font-size: 1rem;
  text-align: justify;
`;

const SocialIcons = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;

  & > * {
    margin-right: 10px;
    font-size: 2.5rem;
    color: #f7f6f2;
    transition: transform 0.3s ease-in-out;
  }

  & > *:hover {
    animation: ${hoverIcon} 0.3s forwards, ${bounceIcon} 0.5s ease-in-out forwards;
  }
`;

const Button = styled.a`
  display: inline-block;
  
  padding: 10px 20px;
  background-color: #28a745;
  color: white;
  text-decoration: none;
  border-radius: 5px;
  font-size: 1rem;
  text-align: center;

  &:hover {
    background-color: #0056b3;
  }
`;

const About = () => {
  const isLaptop = window.innerWidth >= 768;

  return (
    <Container id="about">
      <ImageContainer>
        <Image src="https://lh3.googleusercontent.com/d/1B3cP8ch7nGD3zlkWd8nPtwGrUJ_nkRuH" alt="Abhishek Pratap Singh" />
        <BackgroundCard laptop={isLaptop} />
      </ImageContainer>
      <TextContainer>
        <Title>About Me</Title>
        <Subtitle>
          Hi, I’m Abhishek Pratap Singh, a Software / Project Engineer currently working with Electro Scientific Solution, Agra, deputed at IRDE, DRDO, Dehradun. I work in an environment focused on high-precision and mission-critical systems, where reliability and engineering discipline are extremely important.
          <br/><br/>
          I have a strong background in Information Technology and have built multiple real-world full-stack projects using the MERN stack, Firebase, REST APIs, and secure authentication systems (JWT, RBAC, SSO). These projects helped me understand how to design and build scalable, secure, and user-friendly applications.
          <br/><br/>
          Currently, I am expanding my skill set into C# desktop application development, Qt-based UI systems, and embedded software workflows. While I’m still in the learning phase in system-level development, I actively contribute by working on UI tasks, understanding existing codebases, and supporting integration, testing, and debugging activities.
          <br/><br/>
          I believe in strong fundamentals, clean code, and continuous improvement. I spend my time improving my problem-solving skills, building projects, and learning new technologies. My goal is to grow into a well-rounded software engineer who can work across both application and system-level software.
          <br/><br/>
          I’m always open to learning, collaboration, and exciting opportunities to build meaningful and impactful software.
        </Subtitle>

        <SocialIcons>
          <a href="https://github.com/abhishekpratapsinghbeter" target="_blank" rel="noopener noreferrer">
            <i className="fab fa-github"></i>
          </a>
          <a href="https://www.linkedin.com/in/yourusername" target="_blank" rel="noopener noreferrer">
            <i className="fab fa-linkedin"></i>
          </a>
        </SocialIcons>

        <Button onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}>
          Let's Collaborate
        </Button>
      </TextContainer>

      
    </Container>
  );
};

export default About;
